import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Trash2, Edit, Plus, Tag } from 'lucide-react';
import { toast } from 'sonner';

interface NotificationTag {
  id: string;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
  created: string;
  updated: string;
}

const NotificationTagList: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [tags, setTags] = useState<NotificationTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<NotificationTag | null>(null);
  const [tagToDelete, setTagToDelete] = useState<NotificationTag | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#06C755',
    is_active: true,
  });

  useEffect(() => {
    fetchTags();
  }, [tenantStore.selectedTenant?.id]);

  const fetchTags = async () => {
    if (!tenantStore.selectedTenant?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notification-tags`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTags(data || []);
      } else {
        toast.error('タグの取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      toast.error('タグの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!tenantStore.selectedTenant?.id) return;
    
    const url = editingTag
      ? `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notification-tags/${editingTag.id}`
      : `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notification-tags`;
    
    const method = editingTag ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        toast.success(editingTag ? 'タグを更新しました' : 'タグを作成しました');
        setDialogOpen(false);
        setEditingTag(null);
        setFormData({
          name: '',
          description: '',
          color: '#06C755',
          is_active: true,
        });
        fetchTags();
      } else {
        const error = await response.json();
        toast.error(error.error || '操作に失敗しました');
      }
    } catch (error) {
      console.error('Failed to save tag:', error);
      toast.error('操作に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!tenantStore.selectedTenant?.id || !tagToDelete) return;
    
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notification-tags/${tagToDelete.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        toast.success('タグを削除しました');
        setDeleteDialogOpen(false);
        setTagToDelete(null);
        fetchTags();
      } else {
        const error = await response.json();
        toast.error(error.error || '削除に失敗しました');
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
      toast.error('削除に失敗しました');
    }
  };

  const openEditDialog = (tag: NotificationTag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description,
      color: tag.color,
      is_active: tag.is_active,
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingTag(null);
    setFormData({
      name: '',
      description: '',
      color: '#06C755',
      is_active: true,
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              通知タグ管理
            </CardTitle>
            <Button onClick={openCreateDialog} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              新規作成
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              通知タグがありません
            </div>
          ) : (
            <div className="space-y-4">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <div>
                      <div className="font-medium">{tag.name}</div>
                      {tag.description && (
                        <div className="text-sm text-gray-600">
                          {tag.description}
                        </div>
                      )}
                    </div>
                    <Badge variant={tag.is_active ? 'default' : 'secondary'}>
                      {tag.is_active ? '有効' : '無効'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(tag)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTagToDelete(tag);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTag ? 'タグを編集' : '新規タグ作成'}
            </DialogTitle>
            <DialogDescription>
              通知カテゴリのタグを管理します
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">タグ名</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="例: 新商品情報"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">説明</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="例: 新商品の入荷情報をお知らせします"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="color">カラー</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="w-20"
                />
                <Input
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  placeholder="#06C755"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="rounded"
              />
              <Label htmlFor="is_active">有効にする</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button onClick={handleCreateOrUpdate}>
              {editingTag ? '更新' : '作成'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>タグを削除</DialogTitle>
            <DialogDescription>
              「{tagToDelete?.name}」を削除してもよろしいですか？
              この操作は取り消せません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              削除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default NotificationTagList;